// CapaSource Configuration - UNIFIED APPROACH
// Manages CAPA sources (Internal Audit, External Audit, Customer Complaint, etc.)
// Based on Prisma CapaSource model schema

import { createTrackingConfig } from '../../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../../config/appConfig';

export const capaSourceConfig = {
  entityName: 'CapaSource',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/capa-source',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // CapaSource specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // CAPA sources typically don't have lookup dependencies
      // but you could add severity levels, categories, etc.
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '600px', // Standard width for CAPA sources
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle // From app config: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'capaSourceService',
    methods: {
      getAll: 'getAllCapaSources',
      getById: 'getCapaSourceById', 
      create: 'createCapaSource',
      update: 'updateCapaSource',
      delete: 'deleteCapaSource',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'CAPA Source Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter CAPA source name (e.g., Internal Audit, External Audit, Customer Complaint)',
      table: { header: 'CAPA Source', width: '200px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
        pattern: /^[A-Za-z\s&\-'.()]+$/,
        message: 'CAPA source name must contain only letters, spaces, and common punctuation'
      }
    },
    { 
      name: 'code', 
      label: 'CAPA Source Code', 
      type: 'text', 
      required: true,
      placeholder: 'Enter unique code (e.g., INTERNAL_AUDIT, EXTERNAL_AUDIT, CUSTOMER_COMPLAINT)',
      table: { header: 'Code', width: '120px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 50,
        pattern: /^[A-Z0-9_-]+$/,
        message: 'Code must contain only uppercase letters, numbers, underscores, and hyphens'
      },
      helperText: 'Unique identifier code (typically uppercase, e.g., INTERNAL_AUDIT)'
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      required: false,
      placeholder: 'Enter detailed description of the CAPA source',
      table: { header: 'Description', width: '250px', sortable: true },
      filterable: true,
      validation: { 
        maxLength: 500,
        message: 'Description must not exceed 500 characters'
      }
    },
    { 
      name: 'category', 
      label: 'Category', 
      type: 'text', 
      required: false,
      placeholder: 'Enter category (e.g., AUDIT, COMPLAINT, REVIEW, MONITORING)',
      table: { header: 'Category', width: '120px', sortable: true },
      filterable: true,
      validation: { 
        maxLength: 100,
        message: 'Category must not exceed 100 characters'
      },
      helperText: 'Grouping category for organizing sources'
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'CAPA source is active and available for use',
      description: 'Inactive CAPA sources cannot be assigned to new CAPA records'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (1-999)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { 
        type: 'number',
        min: 1,
        max: 999,
        message: 'Display order must be between 1 and 999'
      },
            helperText: 'Order for display purposes (lower numbers appear first)'
    },
    { 
      name: 'color', 
      label: 'Display Color', 
      type: 'color', 
      required: false,
      placeholder: 'Choose a color or enter hex/name',
      table: { 
        header: 'Color', 
        width: '100px', 
        sortable: false,
        cellRenderer: 'color'
      },
      filterable: false,
      validation: { 
        required: false,
        pattern: /^(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3}|[a-zA-Z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      },
      helperText: 'Used for visual identification in charts and CAPA source indicators'
    },
    { 
      name: 'createdAt', 
      label: 'Created Date', 
      type: 'date', 
      readonly: true,
      table: { 
        header: 'Created', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'dateRange'
    },
    { 
      name: 'updatedAt', 
      label: 'Last Updated', 
      type: 'date', 
      readonly: true,
      table: { 
        header: 'Updated', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'dateRange'
    }
  ],

  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    // Remove system fields that should not be updated
    'id',
    'createdAt',
    'updatedAt', 
    'createdById',
    'updatedById',
    // Remove all relation fields
    'capas'
  ],

  // Filterable fields for search and filtering
  filterableFields: ['name', 'code', 'description', 'category', 'isActive', 'createdAt', 'updatedAt'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'code', 'category', 'isActive']
    },
    {
      name: 'advanced',
      label: 'Advanced Filters',
      fields: ['description', 'record_order', 'color', 'createdAt', 'updatedAt']
    }
  ],

  // Custom sort orders
  customSortOrders: {
    isActive: [true, false], // Active CAPA sources first
    record_order: 'asc', // Ascending order by default
    category: 'asc', // Category alphabetical order
    name: 'asc' // Name alphabetical order
  },

  // Messages Configuration
  messages: {
    loading: 'Loading CAPA sources...',
    noData: 'No CAPA sources found. Click "Add CAPA Source" to create the first source.',
    loadError: 'Failed to load CAPA sources',
    saveSuccess: 'CAPA source saved successfully!',
    saveError: 'Failed to save CAPA source',
    deleteSuccess: 'CAPA source deleted successfully!',
    deleteError: 'Failed to delete CAPA source',
    deleteConfirm: 'Are you sure you want to delete this CAPA source? This action cannot be undone and may affect CAPA records that use this source.',
    activateSuccess: 'CAPA source activated successfully!',
    deactivateSuccess: 'CAPA source deactivated successfully!'
  },

  // Form layout presets
  formLayoutPresets: {
    compact: {
      sections: [
        {
          title: 'Basic Information',
          fields: ['name', 'code', 'category']
        },
        {
          title: 'Details',
          fields: ['description', 'isActive']
        }
      ]
    },
    detailed: {
      sections: [
        {
          title: 'Basic Information',
          fields: ['name', 'code', 'category']
        },
        {
          title: 'Description',
          fields: ['description']
        },
        {
          title: 'Display Settings',
          fields: ['isActive', 'record_order', 'color']
        }
      ]
    }
  },

  // Default form layout
  layout: {
    sections: [
      {
        title: 'Basic Information',
        icon: '📝',
        layout: 'grid',
        gridCols: 'md:grid-cols-2',
        fields: ['name', 'code', 'category']
      },
      {
        title: 'Description',
        icon: '📋',
        layout: 'grid',
        gridCols: 'md:grid-cols-1',
        fields: ['description']
      },
      {
        title: 'Display Settings',
        icon: '🎨',
        layout: 'grid',
        gridCols: 'md:grid-cols-3',
        fields: ['isActive', 'record_order', 'color']
      }
    ]
  },

  // Data transformation function
  transformData: (data) => {
    // Transform form data before sending to backend
    const transformed = { ...data };
    
    // Ensure code is uppercase
    if (transformed.code) {
      transformed.code = transformed.code.toUpperCase();
    }
    
    // Ensure category is properly formatted
    if (transformed.category) {
      transformed.category = transformed.category.trim();
    }
    
    // Handle boolean conversion for isActive
    if (transformed.isActive !== undefined) {
      transformed.isActive = Boolean(transformed.isActive);
    }
    
    // Handle number conversion for record_order
    if (transformed.record_order !== undefined && transformed.record_order !== '') {
      transformed.record_order = parseInt(transformed.record_order, 10);
    }
    
    return transformed;
  },

  // Table configuration
  table: {
    variant: 'default',
    density: 'normal',
    showSelection: true,
    showPagination: true,
    showSearch: true,
    showFilters: true,
    showColumnToggle: true,
    defaultSort: { field: 'record_order', direction: 'asc' }
  },

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('CapaSource'),

  // Form sections configuration
  formSections: [
    {
      id: 'basic',
      title: 'CAPA Source Information',
      icon: '📋',
      columns: 2,
      fields: [
        { name: 'name' },
        { name: 'code' },
        { name: 'category' },
        { name: 'isActive' },
        { name: 'record_order' },
        { name: 'color' }
      ]
    },
    {
      id: 'details',
      title: 'Additional Details',
      icon: '📝',
      columns: 1,
      fields: [
        { name: 'description' }
      ]
    }
  ],

  // Labels and Text Configuration
  labels: {
    singular: 'CAPA Source',
    plural: 'CAPA Sources',
    pageTitle: 'CAPA Source Management',
    pageSubtitle: 'Manage sources that trigger CAPA processes',
    searchPlaceholder: 'Search sources by name, code, or category...',
    viewTitle: 'View CAPA Source',
    noSelectionText: 'Select a CAPA source to see details',
    addNew: 'Add New Source',
    editAction: 'Edit Source',
    
    // Form labels
    basicInfoSection: 'CAPA Source Information',
    detailsSection: 'Additional Details',
    
    // Action labels
    activateCapaSource: 'Activate CAPA Source',
    deactivateCapaSource: 'Deactivate CAPA Source',
    reorderCapaSources: 'Reorder CAPA Sources'
  },

  // Validation rules
  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Export filename for data exports
  exportFileName: 'capa-sources',

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    
    // Convert record_order to integer or remove if empty
    if (transformed.record_order !== undefined) {
      if (transformed.record_order === '' || transformed.record_order === null) {
        // Remove empty record_order field entirely
        delete transformed.record_order;
      } else {
        // Convert to integer
        const numValue = parseInt(transformed.record_order, 10);
        if (!isNaN(numValue)) {
          transformed.record_order = numValue;
        } else {
          delete transformed.record_order; // Remove if not a valid number
        }
      }
    }
    
    // Ensure code is uppercase
    if (transformed.code) {
      transformed.code = transformed.code.toUpperCase();
    }
    
    // Ensure category is properly formatted
    if (transformed.category) {
      transformed.category = transformed.category.trim();
    }
    
    // Handle boolean conversion for isActive
    if (transformed.isActive !== undefined) {
      transformed.isActive = Boolean(transformed.isActive);
    }
    
    return transformed;
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
